import React, { useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSelector } from "react-redux";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { RootState } from "@/store";

const Notes: React.FC = () => {
  const { control, setValue, getValues } = useFormContext();
  const selectedMover = useSelector(
    (state: RootState) => state.movers.selectedMover
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [activeTab, setActiveTab] = useState<string>("customer");

  const customerNoteTemplates =
    selectedMover?.templates.filter(
      (template) => template.category === "customer_notes"
    ) || [];

  const applyTemplate = (templateId: string) => {
    const template = customerNoteTemplates.find(
      (t) => t.id?.toString() === templateId
    );

    if (!template) {
      return;
    }

    const textarea = textareaRef.current;
    const currentValue = getValues("notes_customer") || "";

    if (textarea && textarea === document.activeElement) {
      // Insert at cursor position
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue =
        currentValue.substring(0, start) +
        template.body +
        currentValue.substring(end);
      setValue("notes_customer", newValue);

      // Set cursor position after inserted text
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd =
          start + template.body.length;
      }, 0);
    } else {
      // Prefix if not focused
      setValue(
        "notes_customer",
        template.body + (currentValue ? "\n" + currentValue : "")
      );
    }

    setSelectedTemplateId("");
  };

  return (
    <div>
      <Tabs
        defaultValue="customer"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="customer">Customer Notes</TabsTrigger>
          <TabsTrigger value="internal">Internal Notes</TabsTrigger>
          <TabsTrigger value="personal">Personal Message</TabsTrigger>
        </TabsList>

        <TabsContent value="customer" className="mt-4">
          <div className="flex flex-col gap-2">
            <div className="w-full flex flex-row gap-2">
              <FormField
                control={control}
                name="notes_customer"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex flex-row justify-between items-center gap-2">
                      <FormLabel>Customer notes</FormLabel>
                      <div className="flex flex-col gap-2">
                        <Select
                          value={selectedTemplateId}
                          onValueChange={(value) => {
                            setSelectedTemplateId(value);
                            applyTemplate(value);
                          }}
                        >
                          <SelectTrigger className="h-6">
                            <SelectValue placeholder="Copy from template" />
                          </SelectTrigger>
                          <SelectContent>
                            {customerNoteTemplates.map((template) => (
                              <SelectItem
                                key={template.id}
                                value={template.id?.toString() || ""}
                              >
                                {template.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <FormControl className="mt-2">
                      <Textarea
                        rows={8}
                        {...field}
                        ref={(e) => {
                          textareaRef.current = e;
                        }}
                      />
                    </FormControl>
                    <FormDescription className="mt-2">
                      Customer will see this in the estimate &amp; confirmation.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="internal" className="mt-4">
          <FormField
            control={control}
            name="notes_internal"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Internal notes for your crew/dispatch</FormLabel>
                <FormControl className="mt-2">
                  <Textarea rows={8} {...field} />
                </FormControl>
                <FormDescription className="mt-2">
                  Only visible to your team (sales, operations, etc.).
                </FormDescription>
              </FormItem>
            )}
          />
        </TabsContent>

        <TabsContent value="personal" className="mt-4">
          <FormField
            control={control}
            name="notes_personal_message"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Personal message to the customer</FormLabel>
                <FormControl className="mt-2">
                  <Textarea
                    rows={8}
                    {...field}
                    placeholder="For example: Hi John, I wanted to take a moment to go over the details of your upcoming move to Atlanta and assure you that you're in great hands. At World's Best Movers, we pride ourselves on making the entire moving process seamless and stress-free. We're here to handle every detail, so you can focus on the excitement of settling into your new home! If you have any questions or need anything at all, just let me know. --Vinny"
                  />
                </FormControl>
                <FormDescription className="mt-2">
                  This will be shown on their estimate, both in the email and on
                  their customer portal link.
                  <br />
                  Use this to personalize the estimate for high value customers.
                </FormDescription>
              </FormItem>
            )}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notes;
